import * as THREE from 'three';
import { C } from '../core/palette.js';
import { box, cyl, sphere, textSprite } from '../core/meshkit.js';
import { BreadBoi } from '../entities/breadBoi.js';
import { Townsperson, checkNpcClick } from '../entities/townsfolk.js';
import { HUD } from '../ui/hud.js';
import { getCrumbs, spendCrumbs, addCrumbs, getApartment, addFurniture, hasFurniture, setApartmentTheme } from '../core/save.js';
import { QUESTS, questsForGiver, acceptQuest, turnIn, objectiveProgress, flagObjective } from '../core/quests.js';
import { advanceTime } from '../core/world.js';

// The Upper Crust Apartments — buy furniture, decorate, and sleep to advance time.

export function createApartmentScene(){
  return () => new ApartmentScene();
}

const THEMES = {
  default: { wall: 0xF6E6C9, floor: 0xE9D2A6 },
  cozy:    { wall: 0xE8C9B0, floor: 0xC9A876 },
  modern:  { wall: 0xDDE8F0, floor: 0x9AA0A6 },
  sprout:  { wall: 0xD4E8C5, floor: 0x9CBE93 }
};

const FURNITURE = [
  { id: 'bed',       name: 'Loaf Bed',       icon: '🛏', price: 30,  slot: 0 },
  { id: 'sofa',      name: 'Bagel Sofa',     icon: '🛋', price: 45,  slot: 1 },
  { id: 'lamp',      name: 'Bread Lamp',     icon: '🛋', price: 20,  slot: 2 },
  { id: 'plant',     name: 'Wheat Plant',    icon: '🌾', price: 15,  slot: 3 },
  { id: 'rug',       name: 'Toast Rug',      icon: '🟫', price: 18,  slot: 4 },
  { id: 'painting',  name: 'Bread Boi Print', icon: '🖼', price: 35, slot: 5 }
];

class ApartmentScene {
  constructor(){
    this.scene = new THREE.Scene();
    this.scene.background = new THREE.Color(0xF6E6C9);

    this.camera = new THREE.PerspectiveCamera(50, window.innerWidth/window.innerHeight, 0.1, 100);
    this.camDist = 12;
    this.camTarget = new THREE.Vector3(0, 1.5, 0);
    this.azimuth = 0; this.polar = 0.5;

    this._buildLights();
    this._buildRoom();

    this.super = this._makeSuper();
    this.scene.add(this.super.root);

    this.bread = new BreadBoi();
    this.bread.root.position.set(0, 0, 3.5);
    this.bread.root.rotation.y = Math.PI;
    this.scene.add(this.bread.root);

    this.hotspots = [
      { pos: this.super.root.position.clone(), r: 2.4, label: '🗣 talk to Superintendent Scone', action: () => this._talkToSuper() },
      { pos: new THREE.Vector3(-2.2, 0, -2.2), r: 2.0, label: '🛏 sleep', action: () => this._sleep() },
      { pos: new THREE.Vector3(0, 0, 4.8), r: 2.2, label: '← back to Uptown', action: () => this.manager.goTo('uptown', { from: 'apartment' }) }
    ];
    this.nearSpot = null;
    this.panels = [];
    this.state = 'explore';
  }

  _buildLights(){
    this.scene.add(new THREE.AmbientLight(0xFFF6E8, 0.7));
    const sun = new THREE.DirectionalLight(0xFFF2D8, 0.75);
    sun.position.set(5, 12, 6); sun.castShadow = true;
    sun.shadow.mapSize.set(2048, 2048);
    const s = 14;
    sun.shadow.camera.left = -s; sun.shadow.camera.right = s;
    sun.shadow.camera.top = s; sun.shadow.camera.bottom = -s;
    this.scene.add(sun);
  }

  _buildRoom(){
    const theme = THEMES[getApartment().theme || 'default'];
    // floor
    const floor = new THREE.Mesh(new THREE.PlaneGeometry(12, 12), new THREE.MeshLambertMaterial({ color: theme.floor }));
    floor.rotation.x = -Math.PI/2; floor.receiveShadow = true; this.scene.add(floor);
    // walls (open front)
    box(12, 4, 0.3, theme.wall, 0, 2, -6, this.scene);
    box(0.3, 4, 12, theme.wall, -6, 2, 0, this.scene);
    box(0.3, 4, 12, theme.wall,  6, 2, 0, this.scene);
    // bed placeholder
    box(1.8, 0.5, 2.4, 0xC9A876, -2.2, 0.25, -2.2, this.scene);
    // window
    box(2.2, 1.4, 0.08, 0xDDF3FF, 0, 2.2, -5.85, this.scene);
  }

  _makeSuper(){
    const build = (g) => {
      cyl(.32, .32, .5, 0xC9A876, 0, .6, 0, g, 16); // scone body
      sphere(.28, 0xD9A441, 0, 1.08, 0, g, 16);
      box(.18, .05, .06, 0x2E2018, 0, .98, .24, g);
      box(.04, .04, .04, 0x2E2018, -.09, 1.1, .2, g);
      box(.04, .04, .04, 0x2E2018,  .09, 1.1, .2, g);
      cyl(.03, .03, .32, C.cream, -.4, .7, .1, g, 6);
      cyl(.03, .03, .32, C.cream,  .4, .7, .1, g, 6);
      cyl(.06, .06, .38, 0x5C4033, -.12, .19, 0, g, 6);
      cyl(.06, .06, .38, 0x5C4033,  .12, .19, 0, g, 6);
      const hat = cyl(.3, .3, .08, 0x3D5FC0, 0, 1.4, 0, g, 16);
    };
    return new Townsperson({
      name: 'Superintendent Scone', line: 'make yourself at home.',
      pos: new THREE.Vector3(3.5, 0, -4), facing: Math.PI * 0.7, wander: 0, speed: 0, bubbleY: 2.6, build
    });
  }

  _panel(html, bare){
    const el = document.createElement('div');
    el.className = bare ? '' : 'cafe-panel';
    el.innerHTML = html;
    document.body.appendChild(el);
    this.panels.push(el);
    return el;
  }
  _clearPanels(){ this.panels.forEach(p => p.remove()); this.panels = []; }

  _talkToSuper(){
    this.bread.stop();
    this.bread.drawFace('happy');
    this.superTab = 'quests';
    this._renderSuperDialog();
  }

  _renderSuperDialog(){
    this._clearPanels();
    const q = questsForGiver('superintendent');
    const activeRows = q.active.map(id => this._questRow(id)).join('');
    const availRows = q.available.map(id => this._questRow(id, true)).join('');
    const questBody = activeRows || availRows || '<p class="cafe-sub">No paperwork right now. Enjoy your apartment!</p>';
    const shopBody = this._shopBody();
    const themeBody = this._themeBody();
    const p = this._panel(`
      <div class="cafe-card q-dialog">
        <h2>Superintendent Scone</h2>
        <div class="cafe-sub">Upper Crust Apartments</div>
        <div class="q-tabs">
          <button data-tab="quests" class="cafe-btn ghost ${this.superTab === 'quests' ? 'active' : ''}">quests</button>
          <button data-tab="shop" class="cafe-btn ghost ${this.superTab === 'shop' ? 'active' : ''}">furniture</button>
          <button data-tab="theme" class="cafe-btn ghost ${this.superTab === 'theme' ? 'active' : ''}">theme</button>
        </div>
        <div class="q-tab-body">${this.superTab === 'quests' ? questBody : this.superTab === 'shop' ? shopBody : themeBody}</div>
        <button class="cafe-btn ghost close">done</button>
      </div>
    `);
    p.querySelectorAll('[data-tab]').forEach(b => b.onclick = () => { this.superTab = b.dataset.tab; this._renderSuperDialog(); });
    p.querySelector('.close').onclick = () => this._clearPanels();
    p.querySelectorAll('.accept').forEach(b => b.onclick = () => { acceptQuest(b.dataset.id); this._renderSuperDialog(); });
    p.querySelectorAll('.turnin').forEach(b => {
      b.onclick = () => { turnIn(b.dataset.id); HUD.refreshCrumbs(); HUD.refreshQuests(); this._renderSuperDialog(); };
    });
    p.querySelectorAll('.buy-furn').forEach(b => b.onclick = () => this._buyFurniture(b.dataset.id));
    p.querySelectorAll('.set-theme').forEach(b => b.onclick = () => this._setTheme(b.dataset.theme));
  }

  _shopBody(){
    return FURNITURE.map(f => {
      const owned = hasFurniture(f.id);
      const disabled = owned || getCrumbs() < f.price ? 'disabled' : '';
      return `<div class="q-drow"><div><b>${f.icon} ${f.name}</b><span>${f.slotName || 'decor'} · 🍞 ${f.price}</span></div><button class="cafe-btn buy-furn ${disabled}" data-id="${f.id}">${owned ? 'owned' : 'buy'}</button></div>`;
    }).join('');
  }

  _themeBody(){
    return `<div style="display:flex;flex-direction:column;gap:8px;margin-top:8px;">` +
      Object.keys(THEMES).map(t =>
        `<button class="cafe-btn ghost set-theme ${(getApartment().theme || 'default') === t ? 'active' : ''}" data-theme="${t}">${t}</button>`
      ).join('') +
      `</div>`;
  }

  _buyFurniture(id){
    const f = FURNITURE.find(x => x.id === id);
    if (!f || hasFurniture(id)) return;
    if (spendCrumbs(f.price)){
      addFurniture(id);
      flagObjective('apt-decor', 'boughtFurniture');
      HUD.refreshCrumbs();
      HUD.say(`You bought the ${f.name}!`);
    } else {
      HUD.say('Not enough crumbs for that.');
    }
    this._renderSuperDialog();
  }

  _setTheme(theme){
    setApartmentTheme(theme);
    this._renderSuperDialog();
    // rebuild room with new theme colors by reloading scene — cheap refresh via exit+enter not needed; just tint walls
    this.scene.traverse(o => {
      if (o.isMesh && o.geometry.type === 'BoxGeometry' && o.position.y > 1.5 && Math.abs(o.position.z) > 5){
        o.material.color.setHex(THEMES[theme].wall);
      }
      if (o.isMesh && o.geometry.type === 'PlaneGeometry'){
        o.material.color.setHex(THEMES[theme].floor);
      }
    });
  }

  _questRow(id, accept = false){
    const q = QUESTS[id];
    if (accept) return `<div class="q-drow"><div><b>${q.title}</b><span>${q.desc}</span></div><button class="cafe-btn accept" data-id="${id}">accept</button></div>`;
    const { have, goal, ready } = objectiveProgress(id);
    return `<div class="q-drow ${ready ? 'ready' : ''}"><div><b>${q.title}</b><span>${have}/${goal} ${q.objective.noun}</span></div>${ready ? `<button class="cafe-btn turnin" data-id="${id}">turn in</button>` : ''}</div>`;
  }

  _sleep(){
    this.bread.stop();
    advanceTime(8);
    HUD.refreshClock();
    HUD.say('You took a nice long nap. Pet energy restored!');
    // Could restore pet energy here if save helpers existed; for now just time advance.
  }

  _tap(ray){
    if (this.state !== 'explore') return;
    if (checkNpcClick(ray, this.super, this.bread)) return;

    const dir = ray.ray.direction;
    if (Math.abs(dir.y) < 1e-4) return;
    const t = -ray.ray.origin.y / dir.y;
    if (t <= 0) return;
    const p = ray.ray.origin.clone().addScaledVector(dir, t);
    const gp = new THREE.Vector3(p.x, 0, p.z);
    gp.x = Math.max(-5, Math.min(5, gp.x));
    gp.z = Math.max(-5, Math.min(4, gp.z));
    HUD.hideHint();
    this.bread.walkTo(gp);
  }

  enter({ manager }){
    this.manager = manager;
    this.input = manager.input;
    this.input.useCamera(this.camera);
    this.input.polarRange = [0.28, 0.62];
    this.input.onTap = (ndc, ray) => this._tap(ray);
    this.state = 'explore';
    HUD.setSubtitle('Upper Crust Apartments');
    HUD.say('Your apartment. Talk to Superintendent Scone to furnish it or take a nap.');
    HUD.setHint('click to walk · sleep, decorate, or head back to uptown');
    HUD.hideHintSoon(7000);
  }

  exit(){
    this.input.onTap = null;
    HUD.showEnter(null);
    HUD.say('');
    this._clearPanels();
  }

  update(dt){
    this.bread.update(dt);
    this.super.update(dt, this.bread.root.position);

    let near = null, nd = Infinity;
    this.hotspots.forEach(h => {
      const d = h.pos.distanceTo(this.bread.root.position);
      if (d < nd){ nd = d; near = h; }
    });
    if (near && nd < near.r){
      const key = near.label;
      if (this.nearSpot !== key){
        this.nearSpot = key;
        HUD.showEnter(near.label, () => near.action());
      }
    } else if (this.nearSpot){
      this.nearSpot = null;
      HUD.showEnter(null);
    }

    this.azimuth += (this.input.azimuthGoal - this.azimuth) * 0.08;
    this.polar   += (this.input.polarGoal   - this.polar)   * 0.08;
    this.camTarget.lerp(new THREE.Vector3(this.bread.root.position.x * 0.5, 1.5, this.bread.root.position.z * 0.5 + 1), 0.06);
    this.camera.position.set(
      this.camTarget.x + this.camDist * Math.sin(this.azimuth) * Math.cos(this.polar),
      this.camTarget.y + this.camDist * Math.sin(this.polar),
      this.camTarget.z + this.camDist * Math.cos(this.azimuth) * Math.cos(this.polar)
    );
    this.camera.lookAt(this.camTarget);
  }

  onResize(){
    this.camera.aspect = window.innerWidth / window.innerHeight;
    this.camera.updateProjectionMatrix();
  }
}
